import {
  isPayingAnalyzeOrganization,
  isPayingPublishOrganization,
} from '../../../common/hooks/utils/segmentTraitGetters'
import type { Account } from '../../../common/types'
import {
  canManageBilling,
  getUserCreditCardDetails,
  hasPaymentDetails,
  isCustomerTaxExempt,
  isObjectDismissed,
  isOneBufferOrganization,
  isPaymentPastDue,
  isPayingStripeCustomer,
  isUnrecognizedTaxLocationValid,
  isUserCreditCardExpired,
  isUserCreditCardExpiring,
} from '../../../common/utils/user'

export type BillingCondition =
  | 'payment-past-due'
  | 'expired-card'
  | 'expiring-card'
  | 'missing-billing-address'

export const MULTIPLE_BILLING_ISSUES_SLUG = 'multiple-billing-issues'

/**
 * Timed dismissals are enforced server-side: the backend filters expired
 * entries out of `dismissedObjects` on read, so the banner reappears without
 * any client-side timer logic. Card conditions have no expiry — their
 * dismissal ids carry the card identity instead, so a new card (or an
 * expiring card becoming expired) produces a fresh id and resurfaces the
 * banner.
 */
const DISMISSAL_EXPIRY_DAYS: Partial<Record<BillingCondition, number>> = {
  'payment-past-due': 7,
  'missing-billing-address': 14,
}

export function getBillingConditionDismissalExpiryDays(
  condition: BillingCondition,
): number | null {
  return DISMISSAL_EXPIRY_DAYS[condition] ?? null
}

export function getBillingBannerDismissalId(
  user: Account,
  condition: BillingCondition,
): string {
  if (condition === 'expired-card' || condition === 'expiring-card') {
    const card = getUserCreditCardDetails(user)
    return `billing-banner-${condition}-${card?.last4}-${card?.expMonth}${card?.expYear}`
  }
  return `billing-banner-${condition}`
}

function isPayingOrganization(user: Account): boolean {
  return (
    isPayingStripeCustomer(user) ||
    isPayingPublishOrganization(user) ||
    isPayingAnalyzeOrganization(user)
  )
}

export function getActiveBillingConditions(user: Account): BillingCondition[] {
  const conditions: BillingCondition[] = []

  if (isPaymentPastDue(user) && isOneBufferOrganization(user)) {
    conditions.push('payment-past-due')
  }

  const isInCardConditionScope = Boolean(
    getUserCreditCardDetails(user) &&
    isPayingOrganization(user) &&
    canManageBilling(user) &&
    hasPaymentDetails(user),
  )
  if (isInCardConditionScope) {
    if (isUserCreditCardExpired(user)) {
      conditions.push('expired-card')
    } else if (isUserCreditCardExpiring(user)) {
      conditions.push('expiring-card')
    }
  }

  if (
    isPayingOrganization(user) &&
    !isCustomerTaxExempt(user) &&
    isUnrecognizedTaxLocationValid(user) &&
    canManageBilling(user)
  ) {
    conditions.push('missing-billing-address')
  }

  return conditions
}

export function getVisibleBillingConditions(user: Account): BillingCondition[] {
  return getActiveBillingConditions(user).filter(
    (condition) =>
      !isObjectDismissed(user, getBillingBannerDismissalId(user, condition)),
  )
}

/**
 * Expiring and expired cards were a single banner historically and analytics
 * dashboards track both under `expired-card`, so the expiring state keeps
 * emitting that slug.
 */
export function getBillingBannerSlug(
  conditions: BillingCondition[],
): string | null {
  if (conditions.length === 0) return null
  if (conditions.length > 1) return MULTIPLE_BILLING_ISSUES_SLUG
  return conditions[0] === 'expiring-card' ? 'expired-card' : conditions[0]
}
